import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn, Logo, WEB_LOCALES, type Locale } from '@careandkey/shared';
import { useAuth, signOut } from '../lib/auth';
import { useOrg } from '../lib/org';
import { initials } from '../lib/fmt';
import { supabase } from '../lib/supabase';
import { CommandPalette } from './CommandPalette';

type NavItem = { to: string; key: string; countKey?: string; roles?: string[] };
type NavGroup = { id: string; key: string; items: NavItem[] };
// Finance section is visible only to org_admin, the finance role, and superadmin.
const FINANCE_ROLES = ['org_admin', 'finance', 'superadmin'];
// Procurement (suppliers/POs) is operational + finance.
const PROCUREMENT_ROLES = ['org_admin', 'manager', 'finance', 'superadmin'];

// LOT 0.7 — eight collapsible groups (accordion). Submenu visibility still
// follows the role (per item `roles`); a group is hidden when it has no visible
// item for the current role. Team (Équipe) populates in LOT B.
const NAV_GROUPS: NavGroup[] = [
  { id: 'operations', key: 'nav.groupOperations', items: [
    { to: '/overview', key: 'nav.overview' },
    { to: '/work-orders', key: 'nav.workOrders', countKey: 'workOrders' },
    { to: '/preventive', key: 'nav.preventive' },
    { to: '/requests', key: 'nav.requests', countKey: 'requests' },
    { to: '/checklists', key: 'nav.checklists' },
  ] },
  { id: 'patrimoine', key: 'nav.groupPatrimoine', items: [
    { to: '/properties', key: 'nav.properties', countKey: 'properties' },
    { to: '/assets', key: 'nav.assets', countKey: 'assets' },
    { to: '/due-dates', key: 'nav.dueDates', countKey: 'dueDates' },
  ] },
  { id: 'team', key: 'nav.groupTeam', items: [] },
  { id: 'stock', key: 'nav.groupStock', items: [
    { to: '/parts', key: 'nav.parts' },
    { to: '/tools', key: 'nav.tools' },
  ] },
  { id: 'commercial', key: 'nav.groupCommercial', items: [
    { to: '/leads', key: 'nav.leads' },
    { to: '/marketing', key: 'nav.marketing' },
    { to: '/catalogue', key: 'nav.catalogue' },
    { to: '/commercial-settings', key: 'nav.commercialSettings', roles: ['org_admin', 'superadmin', 'finance'] },
    { to: '/quotes', key: 'nav.quotes' },
    { to: '/contracts', key: 'nav.contracts' },
    { to: '/sales-dashboard', key: 'nav.salesDash' },
  ] },
  { id: 'clientsPartners', key: 'nav.groupClientsPartners', items: [
    { to: '/directory', key: 'nav.directory' },
    { to: '/clients', key: 'nav.clients' },
    { to: '/management-companies', key: 'nav.managementCompanies' },
    { to: '/referrers', key: 'nav.referrers' },
    { to: '/suppliers', key: 'nav.suppliers', roles: PROCUREMENT_ROLES },
  ] },
  { id: 'finance', key: 'nav.groupFinance', items: [
    { to: '/finance-dashboard', key: 'nav.financeDash', roles: FINANCE_ROLES },
    { to: '/invoices', key: 'nav.invoices' },
    { to: '/accounting', key: 'nav.accounting', roles: FINANCE_ROLES },
    { to: '/payables', key: 'nav.payables', roles: FINANCE_ROLES },
    { to: '/treasury', key: 'nav.treasury', roles: FINANCE_ROLES },
    { to: '/pnl', key: 'nav.pnl', roles: FINANCE_ROLES },
    { to: '/statements', key: 'nav.statements', roles: FINANCE_ROLES },
    { to: '/profitability', key: 'nav.profitability', roles: FINANCE_ROLES },
    { to: '/exports', key: 'nav.exports', roles: FINANCE_ROLES },
  ] },
  { id: 'settings', key: 'nav.groupSettings', items: [
    { to: '/users', key: 'nav.users' },
    { to: '/reports', key: 'nav.reports' },
    { to: '/settings', key: 'nav.settings' },
    { to: '/help', key: 'nav.help' },
  ] },
];
const ALL_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);
const FAV_KEY = 'ck.nav.favorites';
const OPEN_GROUPS_KEY = 'ck.nav.openGroups'; // set of independently-open group ids (array in storage)

function loadOpenGroups(fallback: string[]): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore */ }
  return new Set(fallback);
}

function useCounts() {
  const [c, setC] = useState<Record<string, number>>({});
  const [properties, setProperties] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    (async () => {
      const q = (table: string, mod?: (b: any) => any) => {
        let b = supabase.from(table).select('id', { count: 'exact', head: true }).is('deleted_at', null);
        if (mod) b = mod(b);
        return b;
      };
      const [wo, assets, props, due, req, propList] = await Promise.all([
        q('work_orders', (b) => b.in('status', ['open', 'acknowledged', 'assigned', 'in_progress'])),
        q('assets'),
        q('properties'),
        q('asset_obligations', (b) => b.in('status', ['overdue', 'due_soon'])),
        q('service_requests', (b) => b.in('status', ['open', 'triaged'])),
        supabase.from('properties').select('id,name').is('deleted_at', null).order('name'),
      ]);
      setC({
        workOrders: wo.count ?? 0,
        assets: assets.count ?? 0,
        properties: props.count ?? 0,
        dueDates: due.count ?? 0,
        requests: req.count ?? 0,
      });
      setProperties(propList.data ?? []);
    })();
  }, []);
  return { counts: c, properties };
}

function useFavorites(): [string[], (to: string) => void] {
  const [favs, setFavs] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) ?? '[]'); } catch { return []; }
  });
  const toggle = (to: string) =>
    setFavs((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to];
      try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  return [favs, toggle];
}

function AppSwitcher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const apps = [
    { id: 'web', label: t('nav.appBackoffice'), href: null as string | null },
    { id: 'field', label: t('nav.appField'), href: (import.meta as any).env?.VITE_FIELD_URL ?? null },
    { id: 'portal', label: t('nav.appPortal'), href: (import.meta as any).env?.VITE_PORTAL_URL ?? null },
  ];
  return (
    <div className="relative mb-3">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-chrome-border bg-chrome-2 px-3 py-2 text-left text-[13px] text-chrome-fg">
        <span className="truncate"><span className="text-chrome-muted">{t('nav.applications')}:</span> {t('nav.appBackoffice')}</span>
        <span className="shrink-0 text-chrome-muted">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-[10px] border border-chrome-border bg-chrome-2 p-1">
          {apps.map((a) => a.href
            ? <a key={a.id} href={a.href} className="block rounded px-2.5 py-1.5 text-[13px] text-chrome-nav hover:bg-chrome-3">{a.label}</a>
            : <div key={a.id} className={cn('rounded px-2.5 py-1.5 text-[13px]', a.id === 'web' ? 'bg-chrome-3 text-white' : 'text-chrome-muted')}>{a.label}{a.id !== 'web' && ' —'}</div>)}
        </div>
      )}
    </div>
  );
}

function NavRow({ item, counts, isFav, onFav }: { item: NavItem; counts: Record<string, number>; isFav: boolean; onFav: (to: string) => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = pathname.startsWith(item.to);
  const count = item.countKey ? counts[item.countKey] : undefined;
  return (
    <div className="group/row flex items-center">
      <NavLink to={item.to} className={cn('flex min-w-0 flex-1 items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-sm', active ? 'bg-chrome-3 font-medium text-white' : 'text-chrome-nav hover:bg-chrome-2 hover:text-white')}>
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', active ? 'bg-accent-dark' : 'bg-[#3A4456]')} />
        <span className="flex-1 truncate">{t(item.key)}</span>
        {count !== undefined && count > 0 && <span className="font-mono text-xs text-chrome-muted">{count}</span>}
      </NavLink>
      <button
        type="button" aria-label="favorite" aria-pressed={isFav}
        onClick={() => onFav(item.to)}
        className={cn('ml-0.5 px-1 text-sm', isFav ? 'text-accent-dark' : 'text-[#3A4456] opacity-0 group-hover/row:opacity-100 hover:text-chrome-fg')}
      >{isFav ? '★' : '☆'}</button>
    </div>
  );
}

function Sidebar({ counts, properties }: { counts: Record<string, number>; properties: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { role } = useOrg();
  const email = session?.user?.email ?? '';
  const { pathname } = useLocation();
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [favs, toggleFav] = useFavorites();

  // visible groups: filter items by role, drop groups left empty
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((n) => !n.roles || n.roles.includes(role)) }))
    .filter((g) => g.items.length > 0);

  // Independently collapsible groups: each group's open/closed state is remembered
  // (a persisted SET, not a single accordion slot), the group holding the active
  // route is always forced open, and "Collapse all" clears every group.
  const activeGroupId = groups.find((g) => g.items.some((i) => pathname.startsWith(i.to)))?.id;
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => loadOpenGroups(activeGroupId ? [activeGroupId] : groups[0]?.id ? [groups[0].id] : []));
  const persist = (s: Set<string>) => { try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...s])); } catch { /* ignore */ } };
  // when navigation lands in a group, make sure that group is open (don't close others)
  useEffect(() => {
    if (activeGroupId && !openGroups.has(activeGroupId)) {
      setOpenGroups((prev) => { const n = new Set(prev); n.add(activeGroupId); persist(n); return n; });
    }
  }, [activeGroupId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); persist(n); return n; });
  const collapseAll = () =>
    setOpenGroups(() => { const n = new Set<string>(activeGroupId ? [activeGroupId] : []); persist(n); return n; });

  const favItems = ALL_ITEMS.filter((i) => favs.includes(i.to) && (!i.roles || i.roles.includes(role)));

  return (
    <nav className="flex w-[240px] shrink-0 flex-col gap-0.5 overflow-y-auto bg-chrome px-3.5 py-5" aria-label="Main">
      <div className="px-2.5 pb-5 pt-0.5">
        <Logo variant="dark" size={28} />
      </div>
      <AppSwitcher />
      <div className="relative mb-4">
        <button onClick={() => setPortfolioOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-chrome-border bg-chrome-2 px-3 py-2.5 text-left text-[13px] text-chrome-fg">
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            <span className="text-chrome-muted">{t('nav.portfolio')}</span>
            <span className="truncate">{t('nav.allProperties')}</span>
            <span className="font-mono text-chrome-muted">{properties.length}</span>
          </span>
          <span className="shrink-0 text-chrome-muted">▾</span>
        </button>
        {portfolioOpen && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-[10px] border border-chrome-border bg-chrome-2 p-1">
            {properties.map((p) => (
              <div key={p.id} className="rounded px-2.5 py-1.5 text-[13px] text-chrome-nav hover:bg-chrome-3">{p.name}</div>
            ))}
            {properties.length === 0 && <div className="px-2.5 py-1.5 text-2xs text-chrome-muted">{t('common.noData')}</div>}
          </div>
        )}
      </div>

      {favItems.length > 0 && (
        <div className="mb-2">
          <div className="px-2.5 pb-1 text-2xs uppercase tracking-wide text-chrome-muted">{t('nav.favorites')}</div>
          {favItems.map((i) => <NavRow key={`fav-${i.to}`} item={i} counts={counts} isFav onFav={toggleFav} />)}
        </div>
      )}

      <div className="mb-1 flex justify-end px-1">
        <button
          type="button" onClick={collapseAll}
          className="rounded px-1.5 py-0.5 text-2xs uppercase tracking-wide text-chrome-muted hover:text-chrome-fg"
        >{t('nav.collapseAll')}</button>
      </div>

      {groups.map((g) => {
        const isOpen = openGroups.has(g.id);
        const hasActive = g.items.some((i) => pathname.startsWith(i.to));
        return (
          <div key={g.id} className="mb-0.5">
            <button
              onClick={() => toggleGroup(g.id)}
              aria-expanded={isOpen}
              className={cn('flex w-full items-center justify-between rounded-[9px] px-2.5 py-2 text-left text-xs font-medium uppercase tracking-wide', hasActive ? 'text-chrome-fg' : 'text-chrome-muted hover:text-chrome-fg')}
            >
              <span>{t(g.key)}</span>
              <span className="shrink-0 text-chrome-muted">{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
              <div className="mt-0.5 flex flex-col gap-0.5">
                {g.items.map((n) => <NavRow key={n.to} item={n} counts={counts} isFav={favs.includes(n.to)} onFav={toggleFav} />)}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex-1" />
      <button onClick={() => signOut()} className="mt-2 flex items-center gap-2.5 border-t border-[#1C2433] px-2.5 py-3 text-left">
        <div className="grid h-[30px] w-[30px] place-items-center rounded-full bg-[#1C2433] text-xs text-chrome-fg">{initials(email)}</div>
        <div className="flex flex-col">
          <span className="text-[13px] text-chrome-fg">{email.split('@')[0] || t('auth.signOut')}</span>
          <span className="text-xs text-chrome-muted">{t('auth.signOut')}</span>
        </div>
      </button>
    </nav>
  );
}

function OnlineDot() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return (
    <span className={cn('flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-2xs', online ? 'bg-st-done-bg text-st-done-fg' : 'bg-st-overdue-bg text-st-overdue-fg')}>
      ● {online ? t('field.online') : t('field.offline')}
    </span>
  );
}

function LanguageSelector() {
  const { i18n } = useTranslation();
  return (
    <select
      aria-label="language"
      value={i18n.language}
      onChange={(e) => void i18n.changeLanguage(e.target.value as Locale)}
      className="h-9 rounded-md border border-border bg-card px-2 text-sm text-fg outline-none"
    >
      {WEB_LOCALES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
    </select>
  );
}

export function Layout({ header, children }: { header?: ReactNode; children: ReactNode }) {
  const { counts, properties } = useCounts();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="flex min-h-screen bg-bg text-fg">
      <Sidebar counts={counts} properties={properties} />
      <main className="flex min-w-0 flex-1 flex-col">
        {header ?? <Topbar onSearch={() => setPaletteOpen(true)} />}
        {children}
      </main>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

export function Topbar({ actions, onSearch }: { actions?: ReactNode; onSearch?: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-7 py-4">
      <button
        onClick={onSearch ?? (() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true })))}
        className="flex max-w-[460px] flex-1 items-center gap-2 rounded-[10px] border border-border bg-[#F7F8FA] px-3 py-2.5 text-left text-muted"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
        <span className="min-w-0 flex-1 truncate">{t('common.searchPlaceholder')}</span>
        <span className="rounded border border-border bg-card px-1.5 font-mono text-2xs">⌘K</span>
      </button>
      <div className="flex items-center gap-2.5">
        <OnlineDot />
        <LanguageSelector />
        {actions}
      </div>
    </header>
  );
}
